import { useLocation, useNavigate, useRouter } from '@tanstack/react-router';
import {
  type PropsWithChildren,
  createContext,
  useContext,
  useMemo,
} from 'react';
import type { FileRoutesByFullPath } from 'routeTree.gen';
import type { RoutePaths } from 'router/types';
import { isModuleNotFoundError } from 'router/utils';

import { useSignupSteps } from 'hooks/useSignupSteps';
import type { Steps } from 'hooks/useSignupSteps/useSignupSteps';
import { findStepIndex } from 'hooks/useSignupSteps/utils';
import { rumService } from 'services/rum';

type NavigateOptions = Omit<
  Parameters<ReturnType<typeof useNavigate>>[0],
  'to'
>;

export interface StepsContext {
  total: number;
  current: number;
  next: (navigateOptions?: NavigateOptions) => void;
  prev: (navigateOptions?: NavigateOptions) => void;
  preloadNext: () => Promise<void>;
  preloadPrev: () => Promise<void>;
  jump: (step: number, navigateOptions?: NavigateOptions) => void;
  steps: Steps;
}

const StepsContext = createContext<StepsContext>({
  total: 1,
  current: 1,
  next: () => null,
  prev: () => null,
  preloadNext: () => Promise.resolve(),
  preloadPrev: () => Promise.resolve(),
  jump: () => null,
  steps: {},
});

export function StepsProvider({ children }: PropsWithChildren) {
  const signupSteps = useSignupSteps();
  const location = useLocation();

  const steps = useMemo(() => {
    return signupSteps.steps;
  }, [signupSteps]);

  const currentStep = findStepIndex(steps, location.pathname);

  const navigate = useNavigate();
  const router = useRouter();

  const value = useMemo(() => {
    const totalSteps = Object.keys(steps).length;

    const nextStep = currentStep + 1;
    const nextStepPath =
      nextStep === totalSteps
        ? `/success`
        : (steps[nextStep]?.fullPath as RoutePaths);
    const previousStep = currentStep - 1;
    const previousStepPath =
      previousStep === 1 || currentStep === 1
        ? `/`
        : (steps[previousStep]?.fullPath as RoutePaths);

    async function preload(path: keyof FileRoutesByFullPath) {
      try {
        const preloadRoute = router.routesByPath[path];
        if (!preloadRoute) {
          return;
        }

        /**
         * Tanstack router does not handle loadRouteChunk error on preload and so the code will log and unhandled error on the console:
         *  - https://github.com/TanStack/router/blob/main/packages/router-core/src/load-matches.ts#L637
         * To avoid that, we'll loadRouteChunk (JS only) first and then preload the route.
         * If JS fails to load, the ErrorComponent will handle it when navigating to the route.
         * */
        await Promise.all([
          router.loadRouteChunk(preloadRoute),
          router.loadRouteChunk(preloadRoute.parentRoute),
        ]);

        await router.preloadRoute(preloadRoute);
      } catch (error) {
        if (isModuleNotFoundError(error as Error)) {
          // Swallow module not found errors during preloading
          // default Error component will handle them when navigating to the route
          return;
        }

        rumService.addError(error);
      }
    }

    const preloadNext = () => preload(nextStepPath);
    const preloadPrev = () => preload(previousStepPath);
    const next = (navigateOptions?: NavigateOptions) => {
      if (currentStep === totalSteps) return;

      navigate({
        ...navigateOptions,
        to: nextStepPath,
      });
    };

    const prev = (navigateOptions?: NavigateOptions) => {
      navigate({
        ...navigateOptions,
        to: previousStepPath ?? '/',
      });
    };

    const jump = (step: number, navigateOptions?: NavigateOptions) => {
      if (step < 1 || step > totalSteps) return;

      const jumpStepPath =
        step === totalSteps ? `/success` : steps[step]?.fullPath;

      navigate({
        ...navigateOptions,
        to: jumpStepPath,
      });
    };

    return {
      steps,
      total: totalSteps,
      current: currentStep,
      next,
      prev,
      preloadNext,
      preloadPrev,
      jump,
    };
  }, [steps, currentStep, router, navigate]);

  return (
    <StepsContext.Provider value={value}>{children}</StepsContext.Provider>
  );
}

export function useSteps() {
  const value = useContext(StepsContext);
  return value;
}
